import Button from 'components/ReusableComponents/Button'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import { useEmailSubscriptionGate } from 'components/ReusableComponents/UnlockServiceDialog'
import {
  EmailEditorProvider,
  useEmailEditor
} from 'features/EmailEditor/EmailEditorContext'
import EmailEditorPanel from 'features/EmailEditor/EmailEditorPanel'
import EmailPreview from 'features/EmailEditor/EmailPreview'
import TestEmail from 'features/EmailEditor/TestEmail'
import inviteEmailDesignApi from 'services/api/inviteEmailDesign'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useToasts } from 'react-toast-notifications'

import styles from './InviteEmailDesign.module.scss'

const DESIGN_PREVIEW_EMAIL_WIDTH = 680

const DesignFitWidthEmailPreview = () => {
  const { previewHtml } = useEmailEditor()
  const viewportRef = useRef(null)
  const contentRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [layoutHeight, setLayoutHeight] = useState(null)

  useLayoutEffect(() => {
    let frame = 0

    function updateScale() {
      const viewport = viewportRef.current
      const content = contentRef.current
      if (!viewport || !content) {
        return
      }

      const nextScale = Math.min(
        Math.max(viewport.clientWidth / DESIGN_PREVIEW_EMAIL_WIDTH, 0.01),
        1
      )
      const nextHeight = Math.ceil((content.scrollHeight || 0) * nextScale)

      setScale(current => (current === nextScale ? current : nextScale))
      setLayoutHeight(current => (current === nextHeight ? current : nextHeight))
    }

    function scheduleUpdate() {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updateScale)
    }

    updateScale()
    const animationFrame = requestAnimationFrame(updateScale)
    const imageLoadTimeout = window.setTimeout(updateScale, 120)
    window.addEventListener('resize', scheduleUpdate)

    const images = contentRef.current ? contentRef.current.querySelectorAll('img') : []
    images.forEach(image => {
      if (!image.complete) {
        image.addEventListener('load', scheduleUpdate)
      }
    })

    return () => {
      cancelAnimationFrame(animationFrame)
      cancelAnimationFrame(frame)
      window.clearTimeout(imageLoadTimeout)
      window.removeEventListener('resize', scheduleUpdate)
      images.forEach(image => {
        image.removeEventListener('load', scheduleUpdate)
      })
    }
  }, [previewHtml])

  return (
    <div ref={viewportRef} className={styles.designEmailPreviewViewport}>
      <div
        className={styles.designEmailPreviewShell}
        style={{ height: layoutHeight ? `${layoutHeight}px` : 'auto' }}
      >
        <div
          ref={contentRef}
          className={styles.designEmailPreviewScaled}
          style={{
            width: `${DESIGN_PREVIEW_EMAIL_WIDTH}px`,
            transform: `scale(${scale})`
          }}
        >
          <EmailPreview
            holderClassName={styles.designEmailPreviewHolder}
            skipInitialSectionReset
            hideEditContentButton
          />
        </div>
      </div>
    </div>
  )
}

const EditInviteEmailDesignInner = ({ templateId }) => {
  const { addToast } = useToasts()
  const { promptEmailSubscriptionUpgrade } = useEmailSubscriptionGate()
  const { saveChanges, refreshEmailData, isEditorDataReady } = useEmailEditor()

  const [templateName, setTemplateName] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    inviteEmailDesignApi.getTemplate({ templateId }).then(data => {
      setTemplateName(data?.name || '')
      setIsActive(Boolean(data?.isActive))
    })
  }, [templateId])

  function handleSave() {
    if (promptEmailSubscriptionUpgrade()) {
      return
    }

    setIsSaving(true)
    saveChanges()
      .then(() => {
        addToast('Email design saved.', { appearance: 'success' })
      })
      .catch(() => {
        addToast('Failed to save email design.', { appearance: 'error' })
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  return (
    <div className={styles.designEditPage}>
      <CustomHeader width={1280}>
        <div className={styles.designEditHeaderTitle}>
          <span>{templateName || 'Email design'}</span>
          {isActive && <span className={styles.activeBadge}>Active template</span>}
        </div>
      </CustomHeader>

      <div className={styles.designEditPageInner}>
        <div className={styles.designEditToolbar}>
          <div className={styles.designEditToolbarLeft}>
            <Link className={styles.designEditBreadcrumb} to="/invite-email-design">
              All templates
            </Link>
          </div>

          <div className={styles.designEditToolbarRight}>
            <Button disabled={isSaving} onClick={handleSave}>
              {isSaving ? 'Saving…' : 'Save design'}
            </Button>
          </div>
        </div>

        <div className={styles.designEditorWorkspace}>
          {!isEditorDataReady ? (
            <div className={styles.designEditorLoading}>
              <LoadingIndicator />
            </div>
          ) : (
            <div className={styles.designEditorLayout}>
              <div className={styles.designEditorPanelColumn}>
                <div className={styles.designEditorPanelHolder}>
                  <EmailEditorPanel embedded hideFooter onClose={refreshEmailData} />
                </div>
              </div>
              <div className={styles.designRightColumn}>
                <div className={styles.designEmailPreviewAside}>
                  <DesignFitWidthEmailPreview />
                </div>

                <div className={styles.designTestEmailAside}>
                  <TestEmail />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const EditInviteEmailDesign = () => {
  const { templateId } = useParams()

  return (
    <EmailEditorProvider key={templateId} mode="design" designTemplateId={templateId}>
      <EditInviteEmailDesignInner templateId={templateId} />
    </EmailEditorProvider>
  )
}

export default EditInviteEmailDesign
